import { supabase, Article, ArticleListItem, User } from '../lib/supabase'

// 文章相关 API
export const articleApi = {
  // 获取所有文章列表
  async getArticles(): Promise<ArticleListItem[]> {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('id, title, author, date, summary, cover_image')
        .order('date', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      throw error
    }
  },

  // 获取单篇文章详情
  async getArticle(id: number): Promise<Article | null> {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      throw error
    }
  },

  // 发布新文章
  async createArticle(article: Omit<Article, 'id' | 'created_at'>): Promise<Article> {
    try {
      const { data, error } = await supabase
        .from('articles')
        .insert([article])
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      throw error
    }
  }
}

// 用户认证相关 API
export const authApi = {
  // 注册用户
  async register(username: string, password: string, avatar: string): Promise<{ message: string }> {
    try {
      // 使用真实的邮箱格式
      const email = `${username}@gmail.com`;

      // 使用 Supabase Auth 注册
      const { data, error } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          data: {
            username: username
          }
        }
      });

      if (error) {
        // 提供更友好的错误信息
        if (error.message?.includes('invalid')) {
          throw new Error('用户名格式不正确，请使用字母、数字和下划线');
        } else if (error.message?.includes('password')) {
          throw new Error('密码至少需要6位');
        } else if (error.message?.includes('already registered')) {
          throw new Error('用户名已存在');
        } else {
          throw new Error('注册失败：' + error.message);
        }
      }

      if (!data.user) {
        throw new Error('用户创建失败');
      }

      // 上传头像到 Supabase Storage
      let avatarUrl = '';
      if (avatar) {
        // 生成唯一文件名
        const fileName = `avatar_${data.user.id}_${Date.now()}.png`;
        // base64 转 blob
        const res = await fetch(avatar);
        const blob = await res.blob();
        const { data: storageData, error: storageError } = await supabase.storage
          .from('avatars')
          .upload(fileName, blob, { upsert: true, contentType: 'image/png' });
        if (storageError) {
          throw new Error('头像上传失败');
        }
        // 获取公开 URL
        const { data: publicUrlData } = supabase.storage.from('avatars').getPublicUrl(fileName);
        avatarUrl = publicUrlData.publicUrl;
      }

      // 创建用户记录
      const { error: profileError } = await supabase
        .from('users')
        .insert([{
          username,
          auth_id: data.user.id,
          avatar_url: avatarUrl
        }]);

      if (profileError) {
        throw new Error('创建用户资料失败');
      }

      return { message: '注册成功！请检查邮箱验证邮件。' };
    } catch (error: any) {
      throw error;
    }
  },

  // 用户登录
  async login(username: string, password: string): Promise<{ user: any; session: any }> {
    try {
      const email = `${username}@gmail.com`;

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password
      });

      if (error) {
        // 提供更友好的错误信息
        if (error.message?.includes('Invalid login credentials')) {
          throw new Error('用户名或密码错误');
        } else if (error.message?.includes('Email not confirmed')) {
          throw new Error('请先验证邮箱');
        } else {
          throw new Error('登录失败：' + error.message);
        }
      }

      return data;
    } catch (error: any) {
      throw error;
    }
  },

  // 获取当前用户
  async getCurrentUser(): Promise<User | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) return null;

      const { data, error } = await supabase
        .from('users')
        .select('id, username, avatar_url')
        .eq('auth_id', user.id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return null;
    }
  },

  // 用户登出
  async logout(): Promise<void> {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }
}

// 导出默认 API 对象
const api = {
  articles: articleApi,
  auth: authApi
}

export default api 